/**
 * <s-image-lightbox>
 *   Wraps article content. Clicking an image inside [data-lightbox-content] opens it large in the
 *   <dialog data-lightbox-dialog> inside this element. Prev/next buttons and arrow keys step through the
 *   article's images; Esc, the close button or a click on the backdrop closes it (native <dialog>).
 *   Images inside links, and tiny images (icons, badges), keep their normal behaviour.
 *   Shopify CDN images are requested at 2000px wide for the preview.
 */
class ImageLightbox extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.content = this.querySelector('[data-lightbox-content]');
    this.dialog = this.querySelector('[data-lightbox-dialog]');
    if (!this.content || !this.dialog) return;
    this.image = this.dialog.querySelector('[data-lightbox-image]');
    this.caption = this.dialog.querySelector('[data-lightbox-caption]');
    this.counter = this.dialog.querySelector('[data-lightbox-counter]');
    this.prevButton = this.dialog.querySelector('[data-lightbox-prev]');
    this.nextButton = this.dialog.querySelector('[data-lightbox-next]');

    const markImages = () => {
      this.images().forEach((img) => {
        img.classList.add('lightbox-zoomable');
        if (!img.hasAttribute('tabindex')) img.tabIndex = 0;
        img.setAttribute('role', 'button');
        if (!img.getAttribute('aria-label')) img.setAttribute('aria-label', this.dataset.openLabel || 'Open image');
      });
    };
    markImages();
    this.content.addEventListener('load', markImages, { capture: true, signal });

    this.content.addEventListener('click', (event) => {
      const img = event.target.closest('img');
      if (!img || !this.isZoomable(img)) return;
      event.preventDefault();
      this.open(img);
    }, { signal });
    this.content.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const img = event.target.closest('img');
      if (!img || !this.isZoomable(img)) return;
      event.preventDefault();
      this.open(img);
    }, { signal });

    this.dialog.addEventListener('click', (event) => {
      if (event.target.closest('[data-lightbox-close]') || event.target === this.dialog || event.target.matches('[data-lightbox-stage]')) this.requestClose();
    }, { signal });
    // Esc: play the closing animation instead of the instant native close.
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.requestClose();
    }, { signal });
    this.prevButton?.addEventListener('click', () => this.step(-1), { signal });
    this.nextButton?.addEventListener('click', () => this.step(1), { signal });
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.step(-1);
      if (event.key === 'ArrowRight') this.step(1);
    }, { signal });
    this.dialog.addEventListener('close', () => {
      document.documentElement.style.overflow = '';
      this.trigger?.focus({ preventScroll: true });
    }, { signal });
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  isZoomable(img) {
    if (img.closest('a, [data-no-lightbox]')) return false;
    const width = img.naturalWidth || img.width;
    return width === 0 || width >= 160;
  }

  images() {
    return Array.from(this.content.querySelectorAll('img')).filter((img) => this.isZoomable(img));
  }

  largeSrc(img) {
    const source = img.currentSrc || img.src;
    try {
      const url = new URL(source, window.location.href);
      if (url.hostname.includes('cdn.shopify.com') || url.pathname.includes('/cdn/shop/')) {
        url.pathname = url.pathname.replace(/_(\d+x\d*|\d*x\d+)(?=\.[a-z]+$)/i, '');
        url.searchParams.delete('height');
        url.searchParams.delete('crop');
        url.searchParams.set('width', '2000');
      }
      return url.toString();
    } catch {
      return source;
    }
  }

  open(img) {
    this.trigger = img;
    this.list = this.images();
    this.index = Math.max(0, this.list.indexOf(img));
    this.show();
    if (!this.dialog.open) {
      clearTimeout(this.closeTimer);
      delete this.dialog.dataset.state;
      document.documentElement.style.overflow = 'hidden';
      this.dialog.showModal();
    }
  }

  reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  requestClose() {
    if (!this.dialog.open || this.dialog.dataset.state === 'closing') return;
    if (this.reducedMotion()) {
      this.dialog.close();
      return;
    }
    this.dialog.dataset.state = 'closing';
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => {
      this.dialog.close();
      delete this.dialog.dataset.state;
    }, 320);
  }

  step(direction) {
    if (!this.list || this.list.length < 2) return;
    this.index = (this.index + direction + this.list.length) % this.list.length;
    this.trigger = this.list[this.index];
    if (this.reducedMotion()) {
      this.show();
      return;
    }
    // Cross-fade: fade the current image out, swap, fade the new one in.
    this.image.dataset.swap = 'out';
    clearTimeout(this.swapTimer);
    this.swapTimer = setTimeout(() => {
      this.show();
      requestAnimationFrame(() => { delete this.image.dataset.swap; });
    }, 180);
  }

  show() {
    const img = this.list[this.index];
    this.image.src = img.currentSrc || img.src;
    const large = this.largeSrc(img);
    if (large !== this.image.src) {
      const preload = new Image();
      preload.onload = () => { if (this.list[this.index] === img) this.image.src = large; };
      preload.src = large;
    }
    const alt = img.getAttribute('alt') || '';
    this.image.alt = alt;
    if (this.caption) {
      this.caption.textContent = alt;
      this.caption.hidden = alt === '';
    }
    const many = this.list.length > 1;
    if (this.counter) {
      this.counter.textContent = `${this.index + 1} / ${this.list.length}`;
      this.counter.hidden = !many;
    }
    if (this.prevButton) this.prevButton.hidden = !many;
    if (this.nextButton) this.nextButton.hidden = !many;
  }
}

if (!customElements.get('s-image-lightbox')) customElements.define('s-image-lightbox', ImageLightbox);
